import { useLenis } from "lenis/react";
import { ArrowUpRight } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Reveal } from "./Reveal";

const BRANDS = [
  {
    src: "/images/partner-org.jpg",
    alt: "Young Voices Making Choices",
    name: "young-voices",
    title: "Young Voices Making Choices",
    subtitle: "Turning Purpose-Driven Content Into Audience Growth",
    headline: "Generated 630,000 views on an organic marketing video — increasing subscriptions by 2,300%.",
    challenge: "Young Voices Making Choices needed to increase awareness, strengthen its digital presence, and create content that reaches younger audiences while supporting its broader mission.",
    strategy: "Developed a digital-first content and audience engagement strategy centered on compelling storytelling, influencer and celebrity interviews, social media distribution, and content designed for organic sharing.",
    keyWork: ["Digital and social media strategy", "Influencer and celebrity outreach", "Interview and content development", "Audience engagement strategy", "Organic social growth", "Brand awareness initiatives"],
    impactLabel: "The Impact",
    impact: "Content generated 630,000+ views on organic marketing content, expanding visibility and demonstrating how mission-driven storytelling can translate into meaningful digital reach.",
    principle: "Great content doesn't simply generate impressions — it gives people a reason to participate.",
  },
  {
    src: "/images/americas-deadbeat.webp",
    alt: "America's #1 DeadBeat",
    name: "americas-deadbeat",
    title: "America's #1 DeadBeat",
    subtitle: "Building an Entertainment Brand From Concept to Market",
    headline: "Rebranded the business and all marketing collateral — accelerating revenue growth by 12,300% while reducing CAC by 36%.",
    challenge: "America's #1 DeadBeat began as an original reality entertainment concept centered around accountability, family relationships, healing, and competition. The challenge was transforming the idea into a recognizable, marketable entertainment brand.",
    strategy: "Built the marketing foundation around a provocative concept designed to generate conversation while balancing entertainment with a deeper social message: Fighting for Family. Healing the Past.",
    keyWork: ["Brand strategy and positioning", "Audience and customer personas", "Website strategy", "Social media strategy", "Influencer and creator partnerships", "Talent and partnership outreach", "Merchandise strategy", "Creative direction and visual identity", "Content development", "Sponsorship and monetization strategy", "Pitch and promotional materials"],
    impactLabel: "The Impact",
    impact: "Created an integrated brand ecosystem capable of extending beyond a television concept into digital content, social media, creator partnerships, merchandise, sponsorship opportunities, and audience-driven entertainment.",
    principle: "Build the brand bigger than the product so the audience has multiple ways to enter — and stay in — the ecosystem.",
  },
  {
    src: "/images/yerbae.webp",
    alt: "Yerbaé Sparkling Water",
    name: "yerbae",
    title: "Yerbaé",
    subtitle: "Bringing a Better-for-You Beverage Brand Directly to Consumers",
    headline: "Improved marketing ROI by 17% in one quarter.",
    challenge: "Emerging beverage brands compete in crowded retail environments where consumers have dozens of alternatives. Brand awareness alone isn't enough — the product has to become a real-world experience.",
    strategy: "Supported consumer-facing experiential marketing and promotional activation designed to introduce Yerbaé to target consumers, communicate its better-for-you positioning, encourage product trial, and create positive brand interactions.",
    keyWork: ["Experiential marketing", "Consumer engagement", "Product sampling", "Brand representation", "Product education", "Promotional activation", "On-site consumer feedback", "Brand awareness"],
    impactLabel: "The Value Created",
    impact: "Helped bridge the gap between brand awareness and product experience, giving consumers an opportunity to personally experience the product while reinforcing Yerbaé's health-conscious, active-lifestyle positioning.",
    principle: "Sometimes the shortest customer journey is putting the product directly into the customer's hands.",
  },
  {
    src: "/images/celsius.webp",
    alt: "CELSIUS Live Fit energy drink",
    name: "celsius",
    title: "CELSIUS",
    subtitle: "Connecting a Fitness Lifestyle Brand With Consumers",
    headline: "Improved campaign engagement by 48%.",
    challenge: "For a functional energy brand, marketing must communicate more than product features. Consumers need to understand how the product fits into their lifestyle.",
    strategy: "Supported experiential and consumer engagement initiatives that brought CELSIUS' fitness-focused positioning into real-world interactions with consumers.",
    keyWork: ["Experiential marketing", "Consumer activation", "Product sampling", "Brand representation", "Product education", "Promotional engagement", "Consumer relationship building", "Brand awareness"],
    impactLabel: "The Value Created",
    impact: "Helped translate CELSIUS' lifestyle positioning into direct consumer experiences, creating opportunities for trial, education, brand recognition, and future purchase consideration.",
    principle: "Strong lifestyle brands don't just tell consumers who they are — they create experiences that allow consumers to see themselves in the brand.",
  },
  {
    src: "/images/premier-protein.webp",
    alt: "Premier Protein",
    name: "premier-protein",
    title: "Premier Protein",
    subtitle: "Turning Product Trial Into Brand Connection",
    headline: "Grew event sales by up to 42%.",
    challenge: "Nutrition products compete for attention across an increasingly crowded health, wellness, and fitness category. Winning consumers requires making product benefits understandable, relevant, and memorable.",
    strategy: "Supported experiential marketing initiatives that connected Premier Protein products directly with consumers through sampling, product education, and one-to-one brand engagement.",
    keyWork: ["Product sampling", "Experiential marketing", "Consumer engagement", "Brand representation", "Product education", "Promotional activation", "Brand awareness", "Consumer feedback"],
    impactLabel: "The Value Created",
    impact: "Created direct opportunities for consumers to experience the product, understand its benefits, and develop greater familiarity with the Premier Protein brand.",
    principle: "Trial creates attention. The right experience turns that attention into consideration.",
  },
  {
    src: "/images/herd-therapies.webp",
    alt: "H.E.R.D. Therapies Inc.",
    name: "herd-therapies",
    title: "H.E.R.D. Therapies Inc.",
    subtitle: "Building a Mission-Driven Nonprofit Brand From the Ground Up",
    headline: "Built the brand from creation to donations.",
    challenge: "H.E.R.D. Therapies Inc. required more than a logo or marketing campaign. It needed the foundation of an organization capable of communicating its mission, attracting community support, developing partnerships, and eventually scaling its impact.",
    strategy: "Developed the organization's brand and go-to-market foundation around Horsemanship, Empathy, Relationship, and Development, positioning animal-assisted experiences as a vehicle for connection, personal development, and community impact.",
    keyWork: ["Brand development", "Mission and positioning strategy", "Organizational messaging", "Audience segmentation", "Community outreach strategy", "Board recruitment communications", "Partnership development", "Website and digital strategy", "Fundraising positioning", "Program development", "Marketing infrastructure", "Long-term growth strategy"],
    impactLabel: "The Impact",
    impact: "Established a cohesive organizational and marketing foundation designed to support H.E.R.D. Therapies' evolution from an idea into a recognizable nonprofit brand with a defined mission, target communities, leadership structure, and growth strategy.",
    principle: "Mission creates meaning. Strategy creates the infrastructure required to turn that meaning into measurable impact.",
  },
];

const TrustedBy = () => {
  const lenis = useLenis();

  return (
    <section data-testid="trusted-by-section" className="px-6 md:px-12 py-14 md:py-20 border-b border-line">
      <div className="mx-auto max-w-[100rem]">
        <Reveal>
          <p className="overline-label text-center md:text-left" data-testid="trusted-by-label">
            Trusted by teams behind — click any logo to read the story
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-t border-l border-line">
            {BRANDS.map((b) => (
              <Dialog key={b.name}>
                <DialogTrigger asChild>
                  <button
                    data-testid={`brand-logo-${b.name}`}
                    aria-label={`Read the ${b.title} story`}
                    className="group relative flex h-28 md:h-32 items-center justify-center border-b border-r border-line p-4 cursor-pointer"
                  >
                    <img
                      src={b.src}
                      alt={b.alt}
                      loading="lazy"
                      className="max-h-16 md:max-h-20 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute bottom-1.5 left-0 right-0 text-center text-[8px] uppercase tracking-[0.25em] text-taupe opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      Read the story
                    </span>
                  </button>
                </DialogTrigger>
                <DialogContent
                  data-testid={`brand-story-${b.name}`}
                  className="w-[calc(100%-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto bg-paper border-line rounded-none p-6 md:p-10 gap-0"
                >
                  <p className="overline-label">{b.title}</p>
                  <DialogTitle className="mt-4 font-serif text-3xl md:text-4xl font-light tracking-tight leading-tight">
                    {b.subtitle}
                  </DialogTitle>
                  {b.headline && (
                    <p data-testid={`brand-story-${b.name}-headline`} className="mt-6 border-l-2 border-taupe pl-6 font-serif text-2xl md:text-3xl font-light leading-snug text-ink">
                      {b.headline}
                    </p>
                  )}
                  <div className="mt-8 space-y-8">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-2">The Challenge</p>
                      <p className="text-sm md:text-base font-light text-ash leading-relaxed">{b.challenge}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-2">The Strategy</p>
                      <p className="text-sm md:text-base font-light text-ash leading-relaxed">{b.strategy}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-3">Key Work</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                        {b.keyWork.map((k) => (
                          <li key={k} className="flex gap-3 text-sm font-light text-ash leading-relaxed">
                            <span className="text-taupe shrink-0">—</span>{k}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-2">{b.impactLabel}</p>
                      <p className="text-sm md:text-base font-light text-ash leading-relaxed">{b.impact}</p>
                    </div>
                  </div>
                  <blockquote className="mt-10 border-l-2 border-taupe pl-6">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-2">Makkar Growth Principle</p>
                    <p className="font-serif italic text-xl md:text-2xl font-light leading-relaxed">{b.principle}</p>
                  </blockquote>
                  <DialogClose asChild>
                    <button
                      data-testid={`brand-story-${b.name}-cta`}
                      onClick={() => lenis?.scrollTo("#book", { offset: -72, duration: 1.6 })}
                      className="group mt-10 inline-flex items-center gap-2 bg-ink text-paper text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-taupe transition-colors duration-300"
                    >
                      Start your story — book a call
                      <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </DialogClose>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default TrustedBy;
